import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, statusColor } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Broadcast, ArrowLeft, ChartBar, Users, User, Gavel, Scroll, Gear, MonitorPlay, ClipboardText } from "@phosphor-icons/react";
import TeamsTab from "@/pages/manage/TeamsTab";
import PlayersTab from "@/pages/manage/PlayersTab";
import OverviewTab from "@/pages/manage/OverviewTab";
import ReportsTab from "@/pages/manage/ReportsTab";
import AuditTab from "@/pages/manage/AuditTab";
import SettingsTab from "@/pages/manage/SettingsTab";
import OverlaysTab from "@/pages/manage/OverlaysTab";
import RegistrationsTab from "@/pages/manage/RegistrationsTab";

const BASE_TABS = [
  { key: "overview", label: "Overview", Icon: ChartBar },
  { key: "teams", label: "Teams", Icon: Users },
  { key: "players", label: "Players", Icon: User },
  { key: "registrations", label: "Registrations", Icon: ClipboardText },
  { key: "reports", label: "Reports", Icon: Gavel },
  { key: "overlays", label: "Overlays", Icon: MonitorPlay },
  { key: "settings", label: "Settings", Icon: Gear },
];
const AUDIT_TAB = { key: "audit", label: "Audit Log", Icon: Scroll };

export default function AuctionManage() {
  const { auctionId } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const initialTab = new URLSearchParams(loc.search).get("tab") || "overview";
  const [tab, setTab] = useState(initialTab);
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);

  const tabs = useMemo(() => {
    // Audit log is superadmin-only
    if (isSuperadmin) {
      return [...BASE_TABS.slice(0, 4), AUDIT_TAB, ...BASE_TABS.slice(4)];
    }
    return BASE_TABS;
  }, [isSuperadmin]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/auctions/${auctionId}`);
      setAuction(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [auctionId]);

  const setTabAndUrl = (t) => {
    setTab(t);
    nav(`/auction/${auctionId}/manage?tab=${t}`, { replace: true });
  };

  if (loading) return <div className="label-mono py-24 text-center">Loading…</div>;
  if (!auction) return null;

  return (
    <div className="page-dark -mx-4 sm:-mx-6 px-4 sm:px-6 py-6 -my-6 sm:-my-8">
    <div className="space-y-6" data-testid="auction-manage">
      <div>
        <Link to="/dashboard" className="text-xs text-gray-500 hover:text-blue-600 inline-flex items-center gap-1 mb-3">
          <ArrowLeft size={12} /> All Auctions
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div className="min-w-0">
            <div className="label-mono flex items-center gap-2 mb-2">
              <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded border ${statusColor(auction.status)}`}>
                {auction.status === "live" && <span className="live-dot mr-1 inline-block align-middle" />}
                {auction.status}
              </span>
              {auction.venue && <span>· {auction.venue}</span>}
            </div>
            <h1 className="font-display uppercase text-3xl sm:text-4xl leading-none truncate">{auction.name}</h1>
            {auction.description && <p className="text-gray-500 mt-2 max-w-2xl text-sm">{auction.description}</p>}
          </div>
          <div className="flex items-center gap-2">
            {auction.status === "live" ? (
              <Link to={`/auction/${auctionId}/live`} className="btn-primary !px-4 !py-2 text-xs flex items-center gap-2 flex-shrink-0" data-testid="open-live-auction">
                <Broadcast weight="fill" size={14} /> Live Auction
              </Link>
            ) : (
              <span
                className="btn-primary !px-4 !py-2 text-xs flex items-center gap-2 flex-shrink-0 opacity-50 cursor-not-allowed"
                data-testid="open-live-auction-disabled"
                title={`Live Auction is available only when auction status is 'live'. Current: ${auction.status}`}
              >
                <Broadcast weight="fill" size={14} /> Live Auction
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <div className="flex gap-1" data-testid="manage-tabs">
          {tabs.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTabAndUrl(key)}
              data-testid={`tab-${key}`}
              className={`px-4 py-3 text-xs font-bold tracking-widest uppercase whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${
                tab === key ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-blue-600"
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </div>

      <div>
        {tab === "overview" && <OverviewTab auction={auction} reload={load} />}
        {tab === "teams" && <TeamsTab auction={auction} />}
        {tab === "players" && <PlayersTab auction={auction} />}
        {tab === "registrations" && <RegistrationsTab auction={auction} />}
        {tab === "reports" && <ReportsTab auction={auction} />}
        {tab === "audit" && isSuperadmin && <AuditTab auction={auction} />}
        {tab === "overlays" && <OverlaysTab auction={auction} />}
        {tab === "settings" && <SettingsTab auction={auction} reload={load} />}
      </div>
    </div>
    </div>
  );
}
